import { useSyncExternalStore } from 'react';

/**
 * A tiny global polite live-region channel (build-plan §7.3, §7.7). Decouples "announce this" from the
 * component that triggers it (device change, selection, preview toggle), so any code can call
 * `announce('Previewing on tablet')` and the single `<LiveRegion>` mounted in the shell reads it out.
 *
 * It is event-driven (no polling). The message is cleared shortly after so the same text announced twice
 * in a row is still read (a screen reader re-announces only when the text node changes).
 */
let message = '';
const listeners = new Set<() => void>();
let clearTimer: ReturnType<typeof setTimeout> | undefined;

function emit(): void {
  for (const l of listeners) l();
}

export function announce(text: string): void {
  message = text;
  emit();
  if (clearTimer) clearTimeout(clearTimer);
  // Clear after a beat so a repeated identical message still produces a DOM text change next time.
  clearTimer = setTimeout(() => {
    message = '';
    emit();
  }, 1000);
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useLiveMessage(): string {
  return useSyncExternalStore(
    subscribe,
    () => message,
    () => message,
  );
}
